import { Stack, Typography } from '@mui/material'
import React, { useContext, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { type Location, useLocation } from 'react-router-dom'

import PageContainer from 'components/common/PageContainer'
import PageSection from 'components/common/PageSection'
import { Header } from 'components/navigation/Header'
import { StatusCheckBreadcrumbs } from 'components/status-checks/common/StatusCheckBreadcrumbs'
import { CreateStatusCheckForm } from 'components/status-checks/create/CreateStatusCheckForm'
import { EditStatusCheckFormContainer } from 'components/status-checks/edit/EditStatusCheckFormContainer'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { formatExistingStatusCheck } from 'utils/team-external-integrations/team-external-integrations-util'

import type { UserDefinedService } from 'types/api/services/service'
import type {
  ServiceStatusCheck,
  ServiceStatusCheckFromStatusCheck,
  StatusCheck
} from 'types/api/status-checks/status-check'

// The statusCheck passed into location can be of either type ServiceStatusCheck or StatusCheck
// See note in `StatusChecksContext` about supporting both types
// TODO: EN-6581
type LocationState = {
  referrer?: string
  service?: UserDefinedService
  statusCheck?: StatusCheck | ServiceStatusCheckFromStatusCheck
} | null

export const CREATE_STATUS_CHECK_HEADER_DESCRIPTION =
  "First, authenticate to your observability tool and then tell Gremlin which monitor or alert will help us automatically assess if your Service or Scenario is failing. If you don't have your monitors set up yet, we recommend setting up monitors that look at: latency, traffic, error rate, and resource saturation."

// Type guarding to first check which type of statusCheck was passed in
function getIsStatusCheck(
  statusCheck: StatusCheck | ServiceStatusCheck
): statusCheck is StatusCheck {
  // Chose a field that is required and exists only in StatusCheck and not ServiceStatusCheck
  // We can't choose `identifier` because that gets overwritten in the cloning flow
  return 'createdAt' in statusCheck
}

function getFormattedStatusCheck(
  statusCheckFromLocationState:
    | StatusCheck
    | ServiceStatusCheckFromStatusCheck
    | undefined
) {
  if (!statusCheckFromLocationState) {
    return
  }
  return getIsStatusCheck(statusCheckFromLocationState)
    ? formatExistingStatusCheck(statusCheckFromLocationState)
    : statusCheckFromLocationState
}

export const CreateStatusCheck: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const location: Location<LocationState> = useLocation()

  const {
    referrer = statusChecksUrls.base(),
    service,
    statusCheck
  } = location.state ?? {}

  const { isFetching: isFetchingStatusChecks, isError: hasErrorStatusChecks } =
    useStatusChecksContext()
  const {
    isCreatingAuthenticationOnly,
    setIsCreatingAuthenticationOnly,
    isFetchingAwsIAMRoleMetadata,
    hasErrorAwsIAMRoleMetadata,
    isEdit,
    setIsEdit,
    isOnboarding,
    setIsOnboarding
  } = useContext(ObservabilityToolAuthenticationContext)

  /* If this is defined, we know we have cloned an existing statusCheck */
  const formattedStatusCheckClone = statusCheck
    ? getFormattedStatusCheck(statusCheck)
    : undefined

  const isFetching = isFetchingStatusChecks || isFetchingAwsIAMRoleMetadata
  const isError = hasErrorStatusChecks || hasErrorAwsIAMRoleMetadata

  useEffect(() => {
    if (isCreatingAuthenticationOnly) {
      setIsCreatingAuthenticationOnly?.(false)
    }
  }, [isCreatingAuthenticationOnly, setIsCreatingAuthenticationOnly])

  useEffect(() => {
    if (isEdit) {
      setIsEdit?.(false)
    }
  }, [isEdit, setIsEdit])

  /**
   * The isOnboarding flag is stored in the ObservabilityToolAuthenticationContext's state and is used to make rendering
   * decisions. Since this context is used in onboarding and non-onboarding workflows, we manually set the flag prior to
   * continuing.
   *
   * This workflow requires the isOnboarding flag value to be false.
   */
  useEffect(() => {
    if (isOnboarding) {
      setIsOnboarding?.(false)
    }
  }, [isOnboarding, setIsOnboarding])

  return (
    <>
      <Helmet
        title={
          formattedStatusCheckClone
            ? 'Clone Health Check'
            : 'Create Health Check'
        }
      />
      <Header>
        <StatusCheckBreadcrumbs
          endLabel={
            formattedStatusCheckClone
              ? 'Clone Health Check'
              : 'Create a Health Check'
          }
          referrer={referrer}
          service={service}
        />
      </Header>
      <PageSection hasError={isError} isLoading={isFetching}>
        <PageContainer>
          <Stack sx={{ rowGap: 3, marginBottom: 3 }}>
            <Typography data-test-create-status-check-title variant="h5">
              Create a new Health Check
            </Typography>
            <Typography data-test-create-status-check-description>
              {CREATE_STATUS_CHECK_HEADER_DESCRIPTION}
            </Typography>
          </Stack>
          {formattedStatusCheckClone ? (
            <EditStatusCheckFormContainer
              serviceName={service?.name}
              statusCheck={formattedStatusCheckClone}
            />
          ) : (
            <CreateStatusCheckForm service={service} />
          )}
        </PageContainer>
      </PageSection>
    </>
  )
}
